<template>
  <div class="module-overview">
    <ContentWrap title="直播概况">
      <div class="summary">
        <div class="summary-icon"><Icon icon="ep:video-camera" /></div>
        <div><b>直播运营中心</b><span>统一管理直播内容、播出状态与观众互动数据</span></div>
      </div>
    </ContentWrap>

    <div class="section-heading"
      ><b>数据分析</b><span>实时掌握直播供给、播出进度与互动表现</span></div
    >
    <el-row :gutter="18" v-loading="loading">
      <el-col v-for="item in metrics" :key="item.label" :xs="12" :sm="8" :lg="6">
        <el-card shadow="hover" class="metric-card">
          <div
            ><span>{{ item.label }}</span
            ><b>{{ format(item.value) }}</b></div
          >
          <i :style="{ color: item.color, background: item.bg }"><Icon :icon="item.icon" /></i>
        </el-card>
      </el-col>
    </el-row>

    <el-row :gutter="20" class="analysis-row">
      <el-col :xs="24" :lg="8">
        <ContentWrap title="直播状态分布">
          <div v-for="item in statusDistribution" :key="item.label" class="distribution-item">
            <span><i :style="{ background: item.color }"></i>{{ item.label }}</span>
            <el-progress
              :percentage="percentage(item.value)"
              :color="item.color"
              :show-text="false"
            />
            <b>{{ item.value }}</b>
          </div>
        </ContentWrap>
      </el-col>
      <el-col :xs="24" :lg="8">
        <ContentWrap title="直播来源">
          <div class="detail-grid">
            <div v-for="item in sourceDistribution" :key="item.label">
              <Icon :icon="item.icon" /><b>{{ item.value }}</b
              ><span>{{ item.label }}</span>
            </div>
          </div>
        </ContentWrap>
      </el-col>
      <el-col :xs="24" :lg="8">
        <ContentWrap title="互动表现">
          <div class="engagement">
            <div
              ><span>场均观看</span><b>{{ format(averageViews) }}</b></div
            >
            <div
              ><span>场均点赞</span><b>{{ format(averageLikes) }}</b></div
            >
            <div
              ><span>点赞互动率</span><b>{{ engagementRate }}%</b></div
            >
          </div>
        </ContentWrap>
      </el-col>
    </el-row>

    <ContentWrap title="最近直播">
      <el-table :data="recent" v-loading="loading">
        <el-table-column prop="title" label="直播标题" min-width="220" />
        <el-table-column label="来源" width="110"
          ><template #default="{ row }">{{
            sourceLabel(row.sourceType)
          }}</template></el-table-column
        >
        <el-table-column label="状态" width="110"
          ><template #default="{ row }"
            ><el-tag :type="statusType(row.status)">{{ statusLabel(row.status) }}</el-tag></template
          ></el-table-column
        >
        <el-table-column prop="totalViewCount" label="观看" width="100" />
        <el-table-column prop="totalLikeCount" label="点赞" width="100" />
        <el-table-column
          prop="scheduledStartTime"
          label="计划时间"
          width="190"
          :formatter="dateFormatter"
        />
      </el-table>
    </ContentWrap>
  </div>
</template>

<script setup lang="ts">
import { ContentWrap } from '@/components/ContentWrap'
import * as Api from '@/api/live/room'
import { dateFormatter } from '@/utils/formatTime'

const loading = ref(false)
const all = ref<any[]>([])
const recent = computed(() => all.value.slice(0, 8))
const format = (value: number) => new Intl.NumberFormat('zh-CN').format(value || 0)
const sum = (key: string) => all.value.reduce((total, item) => total + Number(item[key] || 0), 0)
const countStatus = (status: number) => all.value.filter((item) => item.status === status).length
const countSource = (sourceType: number) =>
  all.value.filter((item) => item.sourceType === sourceType).length
const totalViews = computed(() => sum('totalViewCount'))
const totalLikes = computed(() => sum('totalLikeCount'))
const averageViews = computed(() =>
  all.value.length ? Math.round(totalViews.value / all.value.length) : 0
)
const averageLikes = computed(() =>
  all.value.length ? Math.round(totalLikes.value / all.value.length) : 0
)
const engagementRate = computed(() =>
  totalViews.value ? ((totalLikes.value * 100) / totalViews.value).toFixed(1) : '0.0'
)
const metrics = computed(() => [
  {
    label: '直播总数',
    value: all.value.length,
    icon: 'ep:video-camera',
    color: '#3478ef',
    bg: '#eaf2ff'
  },
  {
    label: '正在直播',
    value: countStatus(30),
    icon: 'ep:video-play',
    color: '#ef4444',
    bg: '#feecec'
  },
  { label: '直播预告', value: countStatus(20), icon: 'ep:clock', color: '#e88a16', bg: '#fff3df' },
  { label: '精彩回放', value: countStatus(50), icon: 'ep:film', color: '#20a66a', bg: '#e8f8f1' },
  {
    label: '待审核',
    value: countStatus(10),
    icon: 'ep:document-checked',
    color: '#8b5cf6',
    bg: '#f2edff'
  },
  { label: '累计观看', value: totalViews.value, icon: 'ep:view', color: '#0891b2', bg: '#e6f7fb' },
  {
    label: '累计点赞',
    value: totalLikes.value,
    icon: 'ep:pointer',
    color: '#db2777',
    bg: '#fcebf4'
  },
  {
    label: '已结束',
    value: countStatus(40),
    icon: 'ep:circle-check',
    color: '#64748b',
    bg: '#eef1f5'
  }
])
const statusDistribution = computed(() => [
  { label: '直播中', value: countStatus(30), color: '#ef4444' },
  { label: '预告', value: countStatus(20), color: '#e88a16' },
  { label: '回放', value: countStatus(50), color: '#20a66a' },
  { label: '草稿/审核', value: countStatus(0) + countStatus(10), color: '#8b5cf6' },
  { label: '结束/归档', value: countStatus(40) + countStatus(70), color: '#64748b' }
])
const sourceDistribution = computed(() => [
  { label: '外部直播', value: countSource(1), icon: 'ep:link' },
  { label: '云直播', value: countSource(2), icon: 'ep:cloudy' },
  { label: '互动直播', value: countSource(3), icon: 'ep:chat-dot-round' },
  { label: '录播首播', value: countSource(4), icon: 'ep:film' }
])
const percentage = (value: number) =>
  all.value.length ? Math.round((value * 100) / all.value.length) : 0
const statusLabels: Record<number, string> = {
  0: '草稿',
  10: '待审核',
  20: '预告',
  30: '直播中',
  40: '已结束',
  50: '回放',
  60: '已取消',
  70: '已归档'
}
const statusLabel = (status: number) => statusLabels[status] || '未知'
const statusType = (status: number) =>
  (status === 30 ? 'danger' : status === 20 ? 'warning' : status === 50 ? 'success' : 'info') as any
const sourceLabels: Record<number, string> = {
  1: '外部直播',
  2: '云直播',
  3: '互动直播',
  4: '录播首播'
}
const sourceLabel = (source: number) => sourceLabels[source] || '未知'

const loadAllRooms = async () => {
  const pageSize = 100
  const first = await Api.getRoomPage({ pageNo: 1, pageSize })
  const rooms = [...(first.list || [])]
  const pageCount = Math.ceil(Number(first.total || rooms.length) / pageSize)
  if (pageCount > 1) {
    const pages = await Promise.all(
      Array.from({ length: pageCount - 1 }, (_, index) =>
        Api.getRoomPage({ pageNo: index + 2, pageSize })
      )
    )
    pages.forEach((page) => rooms.push(...(page.list || [])))
  }
  return rooms
}

onMounted(async () => {
  loading.value = true
  try {
    all.value = await loadAllRooms()
  } finally {
    loading.value = false
  }
})
</script>

<style scoped>
.summary,
.metric-card :deep(.el-card__body),
.distribution-item,
.engagement > div {
  display: flex;
  align-items: center;
}
.summary {
  gap: 14px;
}
.summary-icon {
  width: 46px;
  height: 46px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #eaf2ff;
  color: #3478ef;
  border-radius: 8px;
  font-size: 24px;
}
.summary b,
.summary span {
  display: block;
}
.summary b {
  font-size: 17px;
}
.summary span {
  margin-top: 5px;
  color: #64748b;
}
.section-heading {
  display: flex;
  align-items: baseline;
  gap: 12px;
  margin: 6px 0 15px;
}
.section-heading b {
  font-size: 18px;
}
.section-heading span {
  color: #8491a3;
}
.metric-card {
  margin-bottom: 18px;
}
.metric-card :deep(.el-card__body) {
  height: 120px;
  justify-content: space-between;
}
.metric-card span,
.metric-card b {
  display: block;
}
.metric-card span {
  color: #64748b;
}
.metric-card b {
  margin-top: 8px;
  font-size: 26px;
}
.metric-card i {
  width: 46px;
  height: 46px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  font-size: 23px;
}
.analysis-row {
  margin-top: 2px;
}
.analysis-row :deep(.content-wrap) {
  height: 260px;
}
.distribution-item {
  display: grid;
  grid-template-columns: 90px 1fr 32px;
  gap: 10px;
  margin: 15px 0;
}
.distribution-item span i {
  display: inline-block;
  width: 7px;
  height: 7px;
  margin-right: 7px;
  border-radius: 50%;
}
.detail-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}
.detail-grid > div {
  padding: 15px;
  background: #f6f8fb;
  text-align: center;
}
.detail-grid svg,
.detail-grid b,
.detail-grid span {
  display: block;
  margin: auto;
}
.detail-grid svg {
  font-size: 22px;
  color: #3478ef;
}
.detail-grid b {
  margin-top: 8px;
  font-size: 22px;
}
.detail-grid span {
  margin-top: 5px;
  color: #64748b;
}
.engagement {
  display: grid;
  gap: 10px;
}
.engagement > div {
  justify-content: space-between;
  padding: 13px 15px;
  background: #f6f8fb;
}
.engagement span {
  color: #64748b;
}
.engagement b {
  font-size: 20px;
}
@media (max-width: 992px) {
  .analysis-row :deep(.content-wrap) {
    height: auto;
  }
  .design-card {
    margin-bottom: 16px;
  }
}
@media (max-width: 600px) {
  .design-content {
    align-items: flex-start;
  }
  .device-preview {
    display: none;
  }
}
</style>
